import { type MutableRefObject, forwardRef, useCallback, useRef } from "react"

import type { ControlledMultiselectProps } from "../../model/types"
import { MultiselectDropdown } from "./multiselect-dropdown"
import { MultiselectSearch } from "./multiselect-search"
import { MultiselectTrigger } from "./multiselect-trigger"
import { useControlledMultiselect } from "./useControlledMultiselect"

export const ControlledMultiselect = forwardRef<
	HTMLInputElement,
	ControlledMultiselectProps
>(
	(
		{
			name,
			label,
			options,
			selectedValues,
			onChange,
			onBlur,
			"data-cy": dataCy = "controlled-multiselect",
			disabled = false,
			placeholder,
			searchAriaLabel = "Search options",
			closeAriaLabel = "Close dropdown",
		},
		externalRef,
	) => {
		const hiddenInputRef = useRef<HTMLInputElement>(
			null,
		) as MutableRefObject<HTMLInputElement | null>

		const setHiddenRef = useCallback(
			(el: HTMLInputElement | null) => {
				hiddenInputRef.current = el
				if (typeof externalRef === "function") externalRef(el)
				else if (externalRef) externalRef.current = el
			},
			[externalRef],
		)

		const handleChange = useCallback(
			(newValues: (string | number)[]) => {
				onChange(newValues)

				if (hiddenInputRef.current) {
					const nativeInputValueSetter =
						Object.getOwnPropertyDescriptor(
							HTMLInputElement.prototype,
							"value",
						)?.set
					nativeInputValueSetter?.call(
						hiddenInputRef.current,
						JSON.stringify(newValues),
					)
					hiddenInputRef.current.dispatchEvent(
						new Event("change", { bubbles: true }),
					)
				}
			},
			[onChange],
		)

		const {
			isOpen,
			searchQuery,
			deferredQuery,
			filteredOptions,
			containerRef,
			searchInputRef,
			triggerRef,
			listboxId,
			triggerLabel,
			toggleDropdown,
			handleSearchChange,
			handleKeyDown,
			handleToggleValue,
		} = useControlledMultiselect({
			options,
			selectedValues,
			onChange: handleChange,
			disabled,
			label,
			placeholder,
			dataCy,
		})

		return (
			<div ref={containerRef} className="relative" data-cy={dataCy}>
				<input
					type="hidden"
					ref={setHiddenRef}
					name={name}
					value={JSON.stringify(selectedValues)}
					onBlur={onBlur}
				/>
				{isOpen ? (
					<MultiselectSearch
						searchInputRef={searchInputRef}
						searchQuery={searchQuery}
						placeholder={placeholder ?? label}
						onSearchChange={handleSearchChange}
						onKeyDown={handleKeyDown}
						onClose={toggleDropdown}
						searchAriaLabel={searchAriaLabel}
						closeAriaLabel={closeAriaLabel}
						data-cy={dataCy}
					/>
				) : (
					<MultiselectTrigger
						triggerRef={triggerRef}
						label={label}
						triggerLabel={triggerLabel}
						isOpen={isOpen}
						disabled={disabled}
						listboxId={listboxId}
						hasSelection={selectedValues.length > 0}
						onToggle={toggleDropdown}
						onKeyDown={handleKeyDown}
						data-cy={dataCy}
					/>
				)}

				{isOpen && (
					<MultiselectDropdown
						listboxId={listboxId}
						deferredQuery={deferredQuery}
						filteredOptions={filteredOptions}
						selectedValues={selectedValues}
						onToggleValue={handleToggleValue}
						data-cy={dataCy}
					/>
				)}
			</div>
		)
	},
)

ControlledMultiselect.displayName = "ControlledMultiselect"
